import { useCallback, useState } from 'react';
import type { InternalMessage, ResendSelectionContext } from '../../MessagesV2.types';
import { areAllResendableSelected, resendableOf } from './resendSelection.utils';

/**
 * Row selection for the bulk resend flow.
 *
 * Selection is keyed by `InternalMessage.id`, which is also the id the resend mutation takes, so
 * no mapping is needed between what's ticked and what gets sent.
 */
export const useResendSelection = (): ResendSelectionContext => {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((rows: readonly InternalMessage[]) => {
    setSelectedIds(previous => {
      // Skips SMS rows rather than selecting disabled ones.
      const resendable = resendableOf(rows);
      if (areAllResendableSelected(rows, previous)) {
        const next = new Set(previous);
        resendable.forEach(row => next.delete(row.id));
        return next;
      }
      const next = new Set(previous);
      resendable.forEach(row => next.add(row.id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const deselect = useCallback((ids: readonly string[]) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  return { selectedIds, toggleSelected, toggleSelectAll, clearSelection, deselect };
};
